import {
  ArrowRight,
  BookOpenCheck,
  ChevronDown,
  CircleCheckBig,
  CreditCard,
  Download,
  ExternalLink,
  LayoutDashboard,
  ListChecks,
  Megaphone,
  MessageSquareText,
  Network,
  Printer,
  Receipt,
  ShieldCheck,
  Sparkles,
  Store,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useSeo } from "@/hooks/useSeo";

const postPurchaseJourney = [
  {
    id: "subscription-activation",
    eyebrow: "Stage 1",
    title: "Subscription confirmed and workspace unlocked",
    body: "Once the subscription payment clears, the agent moves out of the gated purchase flow and into the post-subscription setup journey. This is the real start of the operating manual.",
    bullets: [
      "The payment step is the gatekeeper for workspace access",
      "After payment, the platform moves the agent into the setup and launch flow",
      "The manual is intended to guide what happens next across the whole portal",
    ],
    icon: CreditCard,
    image: "/docs/agent-manual-images/01-agent-login.jpeg",
    imageAlt: "Subscription and access activation entry point",
  },
  {
    id: "onboarding-wizard",
    eyebrow: "Stage 2",
    title: "Complete the onboarding wizard first",
    body: "The onboarding wizard is the first stop after a paid plan. It shapes the agent’s identity, positioning, service regions, response rails, and launch readiness before they fully enter the workspace.",
    bullets: [
      "Finish Identity & Brand, Market Positioning, Service Settings, and Launch Readiness",
      "Use this stage to prepare the first impression of the public portal",
      "Treat this as the setup foundation for everything the agent does next",
    ],
    icon: Sparkles,
    image: "/docs/agent-manual-images/23-onboarding-wizard-full.jpeg",
    imageAlt: "Onboarding wizard full page",
  },
  {
    id: "workspace-dashboard",
    eyebrow: "Stage 3",
    title: "Land on the dashboard and understand the workspace",
    body: "After onboarding, the agent reaches the private workspace dashboard. This becomes the operating base for portfolio control, leads, publishing, billing, and agent growth tasks.",
    bullets: [
      "Check the plan, CEA, and profile path pills",
      "Use the top action area for Public Site, Open Manual, Portfolio Sync, and Manage Billing",
      "Use the main tabs to move between Listings, Leads, Marketing Studio, Network, and Create Listing",
    ],
    icon: LayoutDashboard,
    image: "/docs/agent-manual-images/02-agent-dashboard-summary.jpeg",
    imageAlt: "Agent dashboard summary",
  },
  {
    id: "public-home-portal",
    eyebrow: "Stage 4",
    title: "Review the agent public portal home page first",
    body: "From the dashboard, the agent should open the Public Site and understand the public-facing journey from a client’s point of view. The portal home page is the first live trust layer.",
    bullets: [
      "Review the branded hero section and positioning",
      "Check that buyers can understand who the agent helps and what kind of listings they specialise in",
      "Use the public portal as the front door to the entire branded client journey",
    ],
    icon: Store,
    image: "/docs/agent-manual-images/08-public-home-hero.jpeg",
    imageAlt: "Public-facing agent portal home page",
  },
  {
    id: "neighbourhood-guide-watchlist",
    eyebrow: "Stage 5",
    title: "Walk through the Neighbourhood Guide and District Watchlist",
    body: "The agent portal home page already contains exploration tools that help clients browse districts, understand yields, and shortlist locations. Agents should know this section well because it supports discovery-led conversations.",
    bullets: [
      "Use the Neighbourhood Guide to explore district context",
      "Use the District Watchlist to identify the strongest districts to discuss with clients",
      "Treat these sections as trust-building tools, not just design elements",
    ],
    icon: ListChecks,
    image: "/docs/agent-manual-images/16-neighbourhood-and-watchlist-full.jpeg",
    imageAlt: "Combined neighbourhood guide and district watchlist section",
    gallery: [
      {
        src: "/docs/agent-manual-images/17-neighbourhood-guide-d15-state.jpeg",
        alt: "Neighbourhood guide with district selected",
        title: "Neighbourhood Guide — selected district state",
      },
      {
        src: "/docs/agent-manual-images/10-district-watchlist-card.jpeg",
        alt: "District watchlist card",
        title: "District Watchlist — top districts snapshot",
      },
    ],
  },
  {
    id: "public-listings-pages",
    eyebrow: "Stage 6",
    title: "Understand the public listings journey",
    body: "After the public home page, the next important guided path is the full listings journey. Agents should understand how a buyer moves from general inventory browsing into narrower category pages.",
    bullets: [
      "Start on the Listings page for broad inventory browsing",
      "Move into Buy, New Launches, and Rent pages based on buyer intent",
      "Use these routes to understand how your listings are discovered publicly",
    ],
    icon: ExternalLink,
    gallery: [
      {
        src: "/docs/agent-manual-images/11-public-listings-page.jpeg",
        alt: "Public listings page",
        title: "Listings page",
      },
      {
        src: "/docs/agent-manual-images/12-buy-page.jpeg",
        alt: "Buy page",
        title: "Buy page",
      },
      {
        src: "/docs/agent-manual-images/13-new-launches-page.jpeg",
        alt: "New launches page",
        title: "New Launches page",
      },
      {
        src: "/docs/agent-manual-images/14-rent-page.jpeg",
        alt: "Rent page",
        title: "Rent page",
      },
    ],
  },
  {
    id: "public-contact-route",
    eyebrow: "Stage 7",
    title: "Confirm the public Contact route works for enquiries",
    body: "The Contact page is where browsing turns into action. Agents should verify that enquiry routes and contact actions reflect the correct branded client experience.",
    bullets: [
      "Review the Contact page from the public navigation",
      "Check that contact actions align with your own portal presence",
      "Use this as the final step of the public browsing-to-lead journey",
    ],
    icon: MessageSquareText,
    image: "/docs/agent-manual-images/15-contact-page.jpeg",
    imageAlt: "Public contact page",
  },
  {
    id: "portfolio-import",
    eyebrow: "Stage 8",
    title: "Return to dashboard and run portfolio sync",
    body: "After understanding the public site, the agent returns to the dashboard and runs portfolio sync to bring authorised inventory into the workspace. This is how the inventory base is hydrated for ongoing operations.",
    bullets: [
      "Use Portfolio Sync from the dashboard action area",
      "Treat the sync as the source for the portfolio the agent can work from",
      "Review sync status messages so you know whether inventory is ready",
    ],
    icon: ArrowRight,
    image: "/docs/agent-manual-images/18-import-authorised-portfolio.jpeg",
    imageAlt: "Portfolio Sync action on the agent dashboard",
  },
  {
    id: "workspace-listings",
    eyebrow: "Stage 9",
    title: "Manage Listings inside the workspace",
    body: "Once the synced portfolio is in place, the Listings tab becomes the main control center for managing visibility, filters, edits, and inventory review.",
    bullets: [
      "Use the Listings tab to search, filter, and review inventory",
      "Open listing actions to edit, hide, stage for marketing, or inspect details",
      "Understand this tab before using Create Listing for new sovereign inventory",
    ],
    icon: ListChecks,
    image: "/docs/agent-manual-images/11-public-listings-page.jpeg",
    imageAlt: "Listings view used in the manual",
  },
  {
    id: "workspace-leads",
    eyebrow: "Stage 10",
    title: "Work the Leads tab as the CRM inbox",
    body: "After listings are live, the next operating habit is lead follow-up. The Leads tab is where enquiry traffic, intent, and quick actions become part of the daily workflow.",
    bullets: [
      "Use Leads to view captured enquiries tied to the portfolio",
      "Use quick WhatsApp and email actions for first response",
      "Treat this as the daily inbox for listing-driven conversations",
    ],
    icon: MessageSquareText,
    image: "/docs/agent-manual-images/19-leads-workspace.jpeg",
    imageAlt: "Leads workspace inside the dashboard",
  },
  {
    id: "marketing-studio",
    eyebrow: "Stage 11",
    title: "Use Marketing Studio for campaign publishing",
    body: "Marketing Studio is where agents connect social accounts, create campaign drafts, review publishing assets, and run publish or schedule actions.",
    bullets: [
      "Connect the social accounts needed for publishing",
      "Select or initialize a campaign and review the staging cards",
      "Use the Render History & Publish Timeline to verify campaign events",
    ],
    icon: Megaphone,
    image: "/docs/agent-manual-images/03-marketing-studio-overview.jpeg",
    imageAlt: "Marketing Studio overview",
  },
  {
    id: "workspace-network",
    eyebrow: "Stage 12",
    title: "Use the Network tab for partner collaboration",
    body: "The Network area is where agents can work with other agents, respond to requests, and manage collaboration opportunities beyond their own direct listing flow.",
    bullets: [
      "Use the Network tab to explore partner and referral activity",
      "Treat it as the collaboration layer of the agent workspace",
      "Review it after the core listing and lead workflows are already understood",
    ],
    icon: Network,
    image: "/docs/agent-manual-images/20-network-workspace.jpeg",
    imageAlt: "Network workspace inside the dashboard",
  },
  {
    id: "create-listing",
    eyebrow: "Stage 13",
    title: "Open Create Listing for sovereign inventory",
    body: "Create Listing is the route for building or editing new inventory directly inside the agent workspace. This should come after the agent understands the imported portfolio and listing management flow.",
    bullets: [
      "Use Create Listing when you need to add or build sovereign inventory manually",
      "Treat it as a production tool, not just a form",
      "Use it after you already understand how listings behave publicly and privately",
    ],
    icon: CircleCheckBig,
    image: "/docs/agent-manual-images/21-create-listing-workspace.jpeg",
    imageAlt: "Create Listing workspace inside the dashboard",
  },
  {
    id: "manage-billing",
    eyebrow: "Stage 14",
    title: "Manage billing from inside the workspace",
    body: "Agents should also know how to return to billing controls after purchase. The Manage Billing action is the internal entry to ongoing subscription administration.",
    bullets: [
      "Use Manage Billing from the dashboard action area",
      "Return here when you need to review or adjust subscription status",
      "Treat this as part of ongoing workspace ownership, not just sign-up",
    ],
    icon: Receipt,
    image: "/docs/agent-manual-images/22-manage-billing-action.jpeg",
    imageAlt: "Manage Billing action in the dashboard action row",
  },
  {
    id: "public-site-return",
    eyebrow: "Stage 15",
    title: "Keep returning to Public Site as the final quality check",
    body: "Every major update should end with a return to the public portal. This closes the loop between internal workspace activity and the client-facing brand experience.",
    bullets: [
      "After listings, leads, or marketing changes, review the public site again",
      "Use the public portal as the final QA surface for your brand",
      "The full manual should keep the agent thinking in both private-workspace and public-site modes",
    ],
    icon: ShieldCheck,
    image: "/docs/agent-manual-images/07-agent-public-portal.jpeg",
    imageAlt: "Return to the public site as the final review step",
  },
];

const journeyChecklist = [
  "Complete payment and understand that the workspace unlocks only after subscription confirmation",
  "Finish the post-subscription onboarding wizard before treating the portal as live-ready",
  "Review the dashboard action area and tabs so you know where each workflow lives",
  "Open the Public Site and walk the home page, Neighbourhood Guide, and District Watchlist",
  "Review Listings, Buy, New Launches, Rent, and Contact as part of the branded buyer journey",
  "Return to the dashboard and run portfolio sync",
  "Use Listings and Leads before moving into Marketing Studio, Network, and Create Listing",
  "Use Manage Billing and Public Site as recurring control points during ongoing operations",
];

const manualMarkdownHref = "/docs/agent-manual.md";
const manualPdfHref = "/docs/agent-manual.pdf";

export const AgentManualPage = ({ user }) => {
  const [mobileTocOpen, setMobileTocOpen] = useState(false);

  useSeo({
    title: "Agent Manual | SG Prop Portal",
    description: "Post-subscription portal guide for SG Prop Portal agents.",
  });

  const dashboardPath = !user
    ? "/admin/login"
    : ["agent", "pending_agent"].includes(user.role) && user.subdomain_slug
      ? `/agent/${user.subdomain_slug}/dashboard`
      : "/admin";

  const onboardingPath = ["agent", "pending_agent"].includes(user?.role) && user?.subdomain_slug
    ? `/agent/${user.subdomain_slug}/onboarding-wizard`
    : dashboardPath;

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(212,175,55,0.14),_transparent_28%),linear-gradient(180deg,#f7f3ea_0%,#f3eee4_40%,#efe8da_100%)] px-4 py-8 sm:px-6 lg:px-8" data-testid="agent-manual-page">
      <div className="mx-auto max-w-7xl print:max-w-none">
        <div className="rounded-[32px] border border-[#001935]/10 bg-[linear-gradient(135deg,#001935_0%,#042143_48%,#0b2f59_100%)] p-6 text-white shadow-[0_30px_80px_rgba(0,25,53,0.18)] md:p-10 print:shadow-none" data-testid="agent-manual-hero">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="max-w-4xl">
              <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#F5D98C]">Post-Subscription Agent Manual</p>
              <h1 className="mt-4 text-4xl font-semibold leading-none text-white sm:text-5xl lg:text-6xl" data-testid="agent-manual-heading">
                Guide the agent through the whole portal after the plan is purchased.
              </h1>
              <p className="mt-5 max-w-3xl text-sm leading-8 text-white/82 sm:text-base" data-testid="agent-manual-intro-copy">
                This manual now starts from the moment subscription payment is complete and continues through onboarding, dashboard usage, public portal exploration, portfolio sync, listings, leads, Marketing Studio, Network, Create Listing, billing, and the final Public Site review loop.
              </p>
            </div>

            <div className="grid w-full gap-3 sm:w-auto print:hidden" data-testid="agent-manual-actions">
              <Link className="gold-button !justify-center !px-5 !py-3" data-testid="agent-manual-start-onboarding-link" to={onboardingPath}>
                <Sparkles className="mr-2 h-4 w-4" /> Start at Onboarding Wizard
              </Link>
              <Link className="outline-button border-white/20 bg-white/10 text-white hover:border-[#F5D98C] hover:text-white" data-testid="agent-manual-back-dashboard-link" to={dashboardPath}>
                <LayoutDashboard className="mr-2 h-4 w-4" /> Back to Dashboard
              </Link>
              <button className="outline-button border-white/20 bg-white/10 text-white hover:border-[#F5D98C] hover:text-white" data-testid="agent-manual-print-button" onClick={handlePrint} type="button">
                <Printer className="mr-2 h-4 w-4" /> Print-Friendly View
              </button>
              <a className="outline-button border-white/20 bg-white/10 text-white hover:border-[#F5D98C] hover:text-white" data-testid="agent-manual-download-markdown-link" download href={manualMarkdownHref}>
                <Download className="mr-2 h-4 w-4" /> Download Markdown
              </a>
              <a className="outline-button border-white/20 bg-white/10 text-white hover:border-[#F5D98C] hover:text-white" data-testid="agent-manual-download-pdf-link" download href={manualPdfHref}>
                <Download className="mr-2 h-4 w-4" /> Download PDF
              </a>
            </div>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-3" data-testid="agent-manual-hero-cards">
            <div className="rounded-[24px] border border-white/12 bg-white/8 p-5 backdrop-blur">
              <div className="flex items-center gap-3 text-[#F5D98C]"><BookOpenCheck className="h-5 w-5" /><p className="text-xs font-bold uppercase tracking-[0.18em]">Journey scope</p></div>
              <p className="mt-3 text-sm leading-7 text-white/82">From paid plan activation to public portal review, listings, leads, marketing, network, billing, and return-to-site quality checks.</p>
            </div>
            <div className="rounded-[24px] border border-white/12 bg-white/8 p-5 backdrop-blur">
              <div className="flex items-center gap-3 text-[#F5D98C]"><Store className="h-5 w-5" /><p className="text-xs font-bold uppercase tracking-[0.18em]">Portal-first framing</p></div>
              <p className="mt-3 text-sm leading-7 text-white/82">The manual now explains both the private workspace and the public agent portal, so the agent understands the full branded experience.</p>
            </div>
            <div className="rounded-[24px] border border-white/12 bg-white/8 p-5 backdrop-blur">
              <div className="flex items-center gap-3 text-[#F5D98C]"><ShieldCheck className="h-5 w-5" /><p className="text-xs font-bold uppercase tracking-[0.18em]">Access</p></div>
              <p className="mt-3 text-sm leading-7 text-white/82">This remains an authenticated internal guide for agents who have already passed through plan activation and entered the real portal workflow.</p>
            </div>
          </div>
        </div>

        <div className="mt-8 rounded-[30px] border border-[#D4AF37]/20 bg-[#fff8eb] p-6 text-[#5c4921] shadow-[0_20px_60px_rgba(92,73,33,0.08)]" data-testid="agent-manual-status-note">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]">Important provider note</p>
          <p className="mt-3 text-sm leading-8 sm:text-base">
            <strong>AYRSHARE</strong> live publishing and <strong>CREATOMATE</strong> live rendering are currently <strong>STUBBED</strong> until the production API keys are added. The post-subscription portal flow is ready, but live external execution remains gated by credentials.
          </p>
        </div>

        <div className="mt-6 md:hidden print:hidden" data-testid="agent-manual-mobile-toc-wrapper">
          <Collapsible onOpenChange={setMobileTocOpen} open={mobileTocOpen}>
            <div className="rounded-[26px] border border-[#001935]/10 bg-white/92 p-4 shadow-[0_16px_40px_rgba(0,25,53,0.06)]">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Jump menu</p>
                  <p className="mt-2 text-sm text-[#42566f]">Open the section list and jump to the exact portal step you want to explain.</p>
                </div>
                <CollapsibleTrigger asChild>
                  <button className="flex min-h-11 items-center gap-2 rounded-full border border-[#001935]/10 bg-[#fffaf1] px-4 py-2 text-sm font-semibold text-[#001935]" data-testid="agent-manual-mobile-toc-toggle" type="button">
                    Sections
                    <ChevronDown className={`h-4 w-4 text-[#8b6d16] transition-transform ${mobileTocOpen ? "rotate-180" : "rotate-0"}`} />
                  </button>
                </CollapsibleTrigger>
              </div>

              <CollapsibleContent>
                <div className="mt-4 grid gap-2" data-testid="agent-manual-mobile-toc-list">
                  {postPurchaseJourney.map((section) => (
                    <button
                      className="rounded-[18px] border border-[#001935]/10 bg-white px-4 py-3 text-left text-sm font-semibold text-[#001935] transition hover:border-[#D4AF37]/40 hover:bg-[#fffaf1]"
                      data-testid={`agent-manual-mobile-toc-item-${section.id}`}
                      key={section.id}
                      onClick={() => {
                        const element = document.getElementById(section.id);
                        if (!element) return;
                        element.scrollIntoView({ behavior: "smooth", block: "start" });
                        setMobileTocOpen(false);
                      }}
                      type="button"
                    >
                      <span className="block text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">{section.eyebrow}</span>
                      <span className="mt-1 block">{section.title}</span>
                    </button>
                  ))}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        </div>

        <div className="mt-10 space-y-8" data-testid="agent-manual-sections">
          {postPurchaseJourney.map((section, index) => {
            const SectionIcon = section.icon;
            return (
              <section className="grid gap-6 rounded-[30px] border border-[#001935]/10 bg-white/88 p-5 shadow-[0_18px_50px_rgba(0,25,53,0.06)] md:p-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-10 print:break-inside-avoid" data-testid={`agent-manual-section-${section.id}`} id={section.id} key={section.id}>
                <div className={`${index % 2 === 1 ? "lg:order-2" : ""}`}>
                  {section.image || section.gallery ? (
                    <div className="space-y-4" data-testid={`agent-manual-media-card-${section.id}`}>
                      {section.image ? (
                        <div className="overflow-hidden rounded-[24px] border border-[#001935]/10 bg-[#f6f1e6] shadow-[0_18px_40px_rgba(0,25,53,0.08)]" data-testid={`agent-manual-image-card-${section.id}`}>
                          <img alt={section.imageAlt} className="w-full object-contain object-top" data-testid={`agent-manual-image-${section.id}`} src={section.image} />
                        </div>
                      ) : null}

                      {section.gallery?.length ? (
                        <div className="grid gap-5" data-testid={`agent-manual-gallery-${section.id}`}>
                          {section.gallery.map((item, galleryIndex) => (
                            <figure className="overflow-hidden rounded-[24px] border border-[#001935]/10 bg-[#f6f1e6] shadow-[0_18px_40px_rgba(0,25,53,0.08)]" data-testid={`agent-manual-gallery-item-${section.id}-${galleryIndex + 1}`} key={item.src}>
                              <img alt={item.alt} className="w-full object-contain object-top" src={item.src} />
                              <figcaption className="border-t border-[#001935]/8 bg-white/92 px-4 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-[#6b6252]">
                                {item.title}
                              </figcaption>
                            </figure>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="flex h-full min-h-[240px] flex-col justify-between rounded-[24px] border border-[#001935]/10 bg-[linear-gradient(180deg,#fffaf1_0%,#f6ecdc_100%)] p-6 shadow-[0_18px_40px_rgba(0,25,53,0.06)]" data-testid={`agent-manual-image-card-${section.id}`}>
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#001935] text-[#F5D98C]">
                        <SectionIcon className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#8b6d16]">Portal step</p>
                        <p className="mt-3 text-2xl font-semibold text-[#001935]">{section.title}</p>
                        <p className="mt-3 text-sm leading-7 text-[#42566f]">This part of the guide explains the intended operating journey even where a dedicated screenshot has not yet been added.</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col justify-center">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#fff4dd] text-[#8b6d16]" data-testid={`agent-manual-section-icon-${section.id}`}>
                      <SectionIcon className="h-5 w-5" />
                    </span>
                    <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#8b6d16]">{section.eyebrow}</p>
                  </div>
                  <h2 className="mt-4 text-3xl font-semibold text-[#001935] sm:text-4xl" data-testid={`agent-manual-section-heading-${section.id}`}>{section.title}</h2>
                  <p className="mt-4 text-sm leading-8 text-[#42566f] sm:text-base" data-testid={`agent-manual-section-body-${section.id}`}>{section.body}</p>
                  <ul className="mt-5 space-y-3" data-testid={`agent-manual-section-bullets-${section.id}`}>
                    {section.bullets.map((bullet) => (
                      <li className="flex items-start gap-3 text-sm leading-7 text-[#42566f] sm:text-base" key={bullet}>
                        <span className="mt-2 h-2.5 w-2.5 rounded-full bg-[#D4AF37]" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            );
          })}
        </div>

        <div className="mt-10" data-testid="agent-manual-summary-grid">
          <section className="rounded-[28px] border border-[#001935]/10 bg-white/88 p-6 shadow-[0_18px_50px_rgba(0,25,53,0.06)] print:break-inside-avoid" data-testid="agent-manual-daily-checklist-card">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#8b6d16]">Recommended walkthrough</p>
            <h2 className="mt-3 text-2xl font-semibold text-[#001935]">What the agent should do in order</h2>
            <ul className="mt-5 space-y-3">
              {journeyChecklist.map((item) => (
                <li className="flex items-start gap-3 text-sm leading-7 text-[#42566f]" key={item}>
                  <span className="mt-2 h-2.5 w-2.5 rounded-full bg-[#D4AF37]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AgentManualPage;